/**
 * The passers-by themselves — geometry and materials only.
 *
 * Built in code from boxes, exactly as `plants.ts`, `pavilion.ts` and
 * `horizon.ts` are. No downloaded model, no asset pipeline, no licence to
 * track, nothing added to the payload a first-time visitor downloads over
 * cellular data (AGENTS.md, "Keep the payload small").
 *
 * ## One crowd, six draw calls
 *
 * Every figure is drawn from the same five `InstancedMesh`es — torso, head,
 * headwear, legs, arms — sized for the whole crowd up front. Six figures cost
 * exactly what one does, which is the only way a crowd is affordable on top
 * of a full tier-3 plot's ~405 calls with a camera feed underneath
 * (AGENTS.md, "Instance anything repeated"). Per-figure colour rides on
 * `instanceColor`, so a kind is a palette rather than a mesh.
 *
 * A skinned rig was the obvious alternative and is the wrong one here: a
 * skeleton costs a shader variant, a bone texture and a per-figure draw, to
 * animate six boxes. The walk is composed straight into the instance
 * matrices instead — legs and arms are authored with their origin at the
 * joint, so a swing is a rotation about the instance's own origin.
 *
 * ## The bubble
 *
 * One small camera-facing plane per figure, textured from a canvas drawn once
 * at build time. A glyph rendered into a 192 px canvas stays crisp on a phone
 * where geometry-built faces at this scale would be three triangles of mush,
 * and it costs one texture per reaction — five 192² textures, about the size
 * of nothing. Bubbles are the only per-figure draw calls, and they are the
 * reason the cap in `content/visitors.ts` is a small number.
 *
 * Nothing here is in the plot's pick tree, so no part of a visitor can ever
 * intercept a tap meant for soil or a plant.
 */

import * as THREE from 'three';
import { REACTIONS, type ReactionId, type VisitorKind } from '../content/visitors';

/** Everything the figures need to know about one visitor for one frame. */
export interface FigurePose {
  kind: VisitorKind;
  x: number;
  y: number;
  z: number;
  /** Yaw, radians, three.js convention for a model whose forward is +Z. */
  heading: number;
  /** Metres walked. Drives the gait; must not reset while a figure lives. */
  distance: number;
  /** 0–1 how much of the walk cycle to show. Zero while stopped. */
  stride: number;
  reaction: ReactionId | null;
  /** 0–1 bubble opacity. */
  bubble: number;
}

export interface VisitorFigures {
  root: THREE.Group;
  /** Draw this frame's crowd. Anything past `capacity` is ignored. */
  sync(poses: readonly FigurePose[]): void;
  /** Turn the bubbles to face the camera. Call after `sync`. */
  faceCamera(camera: THREE.Camera): void;
  dispose(): void;
}

/**
 * Metres of ground covered per full stride cycle, as a multiple of the
 * figure's height. Too long and the legs scissor like a cartoon; too short
 * and a slow amble turns into a sprint on the spot.
 */
const STRIDE_PER_HEIGHT = 0.62;

/** How far a leg swings at full stride, radians. */
const SWING = 0.55;

/**
 * Build the crowd's shared rig.
 *
 * `capacity` is the hard cap — the instance buffers are allocated for it and
 * never grow, so a bug in the arrival logic cannot cost frame rate.
 * `plotScale` sizes the whole crowd against the plot the way
 * `models/pavilion.ts` scales itself: the garden more than doubles in radius
 * as districts open, and a figure authored in absolute metres would go from
 * passer-by to ant without anybody noticing.
 */
export function buildVisitorFigures(capacity: number, plotScale = 1): VisitorFigures {
  const root = new THREE.Group();
  // Visitors are decoration walking over a garden the player taps. Keeping
  // them off the raycaster's radar entirely is cheaper and more certain than
  // relying on them never being in the pick tree.
  root.raycast = () => {};

  const owned: Array<{ dispose(): void }> = [];
  const track = <T extends { dispose(): void }>(x: T): T => {
    owned.push(x);
    return x;
  };

  const bodyMat = track(
    new THREE.MeshStandardMaterial({ roughness: 0.92, flatShading: true }),
  );

  /** A box whose origin sits at the middle of its top face — a joint. */
  const limb = (w: number, h: number, d: number) => {
    const geo = new THREE.BoxGeometry(w, h, d);
    geo.translate(0, -h / 2, 0);
    return geo;
  };

  const torsoGeo = track(new THREE.BoxGeometry(1, 1, 1));
  const headGeo = track(new THREE.BoxGeometry(1, 1, 1));
  const hatGeo = track(new THREE.ConeGeometry(0.6, 1, 6));
  const legGeo = track(limb(1, 1, 1));
  const armGeo = track(limb(1, 1, 1));

  const mesh = (geo: THREE.BufferGeometry, count: number) => {
    const m = new THREE.InstancedMesh(geo, bodyMat, count);
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.castShadow = true;
    // The figures move every frame and the bounding sphere is computed from
    // instance zero, so leave culling to the plot they stand on.
    m.frustumCulled = false;
    m.count = 0;
    m.raycast = () => {};
    root.add(m);
    return m;
  };

  const torsos = mesh(torsoGeo, capacity);
  const heads = mesh(headGeo, capacity);
  const hats = mesh(hatGeo, capacity);
  const legs = mesh(legGeo, capacity * 2);
  const arms = mesh(armGeo, capacity * 2);
  const parts = [torsos, heads, hats, legs, arms];
  for (const p of parts) owned.push({ dispose: () => p.dispose() });

  /* ---- Reaction bubbles ------------------------------------------- */

  const bubbleTex = new Map<ReactionId, THREE.Texture>();
  for (const reaction of Object.values(REACTIONS)) {
    bubbleTex.set(reaction.id, track(bubbleTexture(reaction.glyph, reaction.tint)));
  }

  const bubbleGeo = track(new THREE.PlaneGeometry(1, 1));
  const bubbles: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>[] = [];
  for (let i = 0; i < capacity; i++) {
    const mat = track(
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
        // Fog is tuned to hide the horizon's far rim and would grey a bubble
        // at three metres by most of its value — the same trap the distant
        // buildings fell into. A bubble is UI drawn in the world; it is not
        // subject to the weather.
        fog: false,
        toneMapped: false,
      }),
    );
    const m = new THREE.Mesh(bubbleGeo, mat);
    m.visible = false;
    m.raycast = () => {};
    // Over the plants it hovers above, never through them.
    m.renderOrder = 4;
    root.add(m);
    bubbles.push(m);
  }

  /* ---- Per-frame -------------------------------------------------- */

  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const colour = new THREE.Color();
  const camQuat = new THREE.Quaternion();
  const parentQuat = new THREE.Quaternion();

  const put = (
    m: THREE.InstancedMesh,
    index: number,
    px: number,
    py: number,
    pz: number,
    yaw: number,
    pitch: number,
    sx: number,
    sy: number,
    sz: number,
    hex: number,
  ) => {
    euler.set(pitch, yaw, 0, 'YXZ');
    quat.setFromEuler(euler);
    pos.set(px, py, pz);
    scale.set(sx, sy, sz);
    m.setMatrixAt(index, matrix.compose(pos, quat, scale));
    m.setColorAt(index, colour.setHex(hex));
  };

  return {
    root,

    sync(poses) {
      const n = Math.min(poses.length, capacity);
      for (let i = 0; i < n; i++) {
        const p = poses[i];
        const k = p.kind;
        const h = k.height * plotScale;

        // Phase of the walk cycle from distance travelled, so the gait is a
        // function of where the figure *is* rather than of frame time — it
        // cannot moonwalk when the frame rate drops or a tab resumes.
        const phase = (p.distance / (h * STRIDE_PER_HEIGHT)) * Math.PI * 2;
        const swing = Math.sin(phase) * SWING * k.gait * p.stride;
        // Two bobs per cycle: one per footfall. Small — at this scale a
        // centimetre is a lurch.
        const bob = (1 - Math.cos(phase * 2)) * 0.5 * 0.018 * h * p.stride;
        const y = p.y + bob;
        const yaw = p.heading;

        // Forward and lateral offsets in the figure's own frame (+Z forward).
        const fwd = Math.sin(yaw);
        const fwdZ = Math.cos(yaw);
        const side = Math.cos(yaw);
        const sideZ = -Math.sin(yaw);

        /*
         * Shoulders sit wider than the torso and hips wider than the legs.
         * The first pass had both flush, and the whole figure read as one
         * slab of colour with a hat on: at 0.3 m a limb only exists if there
         * is visible background between it and the body.
         */
        const hipY = y + h * 0.44;
        const shoulderY = y + h * 0.74;
        const hipHalf = h * 0.085;
        const shoulderHalf = h * 0.175;

        put(
          torsos, i,
          p.x, hipY + h * 0.17, p.z, yaw, 0,
          h * 0.26, h * 0.34, h * 0.15,
          k.garment,
        );
        put(
          heads, i,
          p.x, y + h * 0.87, p.z, yaw, 0,
          h * 0.16, h * 0.16, h * 0.15,
          k.skin,
        );
        put(
          hats, i,
          p.x, y + h * 0.95, p.z, yaw, 0,
          h * 0.2, h * 0.085, h * 0.2,
          k.head,
        );

        for (let s = 0; s < 2; s++) {
          const sign = s === 0 ? 1 : -1;
          const legSwing = swing * sign;
          put(
            legs, i * 2 + s,
            p.x + side * hipHalf * sign,
            hipY,
            p.z + sideZ * hipHalf * sign,
            yaw, legSwing,
            h * 0.085, h * 0.44, h * 0.1,
            k.lower,
          );
          // Arms counter-swing, which is the single cue that reads as walking
          // rather than as a model being slid along the ground.
          put(
            arms, i * 2 + s,
            p.x + side * shoulderHalf * sign,
            shoulderY,
            p.z + sideZ * shoulderHalf * sign,
            yaw, -legSwing * 0.8,
            h * 0.07, h * 0.3, h * 0.075,
            k.skin,
          );
        }

        const bubble = bubbles[i];
        const tex = p.reaction ? bubbleTex.get(p.reaction) : undefined;
        if (tex && p.bubble > 0.002) {
          if (bubble.material.map !== tex) {
            bubble.material.map = tex;
            bubble.material.needsUpdate = true;
          }
          bubble.material.opacity = p.bubble;
          // Rises a little as it appears, which is what makes it read as
          // something the figure thought rather than a decal on its head.
          const lift = h * (1.12 + 0.06 * p.bubble);
          bubble.position.set(p.x + fwd * h * 0.04, y + lift, p.z + fwdZ * h * 0.04);
          bubble.scale.setScalar(h * (0.42 + 0.06 * p.bubble));
          bubble.visible = true;
        } else {
          bubble.visible = false;
        }
      }

      for (let i = n; i < capacity; i++) bubbles[i].visible = false;

      torsos.count = n;
      heads.count = n;
      hats.count = n;
      legs.count = n * 2;
      arms.count = n * 2;
      for (const part of parts) {
        part.instanceMatrix.needsUpdate = true;
        if (part.instanceColor) part.instanceColor.needsUpdate = true;
      }
    },

    faceCamera(camera) {
      camera.getWorldQuaternion(camQuat);
      // The crowd hangs under the plot, which carries the floor's yaw. Undo
      // the parent's rotation or every bubble is turned by it as well.
      root.getWorldQuaternion(parentQuat);
      parentQuat.invert().multiply(camQuat);
      for (const b of bubbles) {
        if (b.visible) b.quaternion.copy(parentQuat);
      }
    },

    dispose() {
      for (const o of owned) o.dispose();
      root.clear();
    },
  };
}

/**
 * Draw one reaction into a texture: a rounded speech bubble with a tail and
 * the glyph in it.
 *
 * Canvas rather than geometry because the whole thing is about 40 px on a
 * phone, and at 40 px a built face is a smear while a glyph is legible. Power
 * of two, mipmapped, and anisotropic so it stays sharp at the glancing angles
 * a garden on a table is mostly viewed from.
 */
function bubbleTexture(glyph: string, tint: number): THREE.Texture {
  const S = 192;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    const pad = S * 0.08;
    const w = S - pad * 2;
    const bodyH = S * 0.72;
    const r = S * 0.2;

    ctx.fillStyle = `#${tint.toString(16).padStart(6, '0')}`;
    ctx.strokeStyle = 'rgba(40, 34, 24, 0.55)';
    ctx.lineWidth = S * 0.035;

    ctx.beginPath();
    ctx.moveTo(pad + r, pad);
    ctx.arcTo(pad + w, pad, pad + w, pad + bodyH, r);
    ctx.arcTo(pad + w, pad + bodyH, pad, pad + bodyH, r);
    // The tail, drawn into the same path so the outline runs round it.
    ctx.lineTo(S * 0.56, pad + bodyH);
    ctx.lineTo(S * 0.44, S - pad * 0.4);
    ctx.lineTo(S * 0.42, pad + bodyH);
    ctx.arcTo(pad, pad + bodyH, pad, pad, r);
    ctx.arcTo(pad, pad, pad + w, pad, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.font = `${Math.round(S * 0.5)}px system-ui, "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#2a2218';
    ctx.fillText(glyph, S / 2, pad + bodyH * 0.5);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}
